import React, { useState, useMemo } from "react";
import AppDataGrid from "../../common/AppDataGrid";
import {
  CircularProgress,
  Box,
  Container,
  Snackbar,
  Alert,
} from "@mui/material";
import useFirestore from "../../../config/fireBaseHook";
import Add from "./inc/Add";
import Edit from "./inc/Edit";
import EditUpload from "../../../hooks/EditUpload";
import ClearCollectionButton from "../../common/ClearCollectionButton";
import TechPrepPortalMgtBreadCrumbs from "../../Layout/TechPrepPortalMgtBreadCrumbs";
import { useAuth } from "../../../config/AuthContext";
import SimpleToolbar from "../../common/SimpleToolbar";
import CustomFilterPanel from "../../common/CustomFilterPanel";
import useCustomFilter from "../../../hooks/useCustomFilter";
import { residenceStatusCodesFilters } from "../filterConfigurations";

const ResidenceStatusCodes = () => {
  const { userPermissions } = useAuth();
  const canEdit = userPermissions["Residence Status Codes"]?.edit;
  const {
    data: residenceStatusCodes,
    loading,
    error,
  } = useFirestore("ResidenceStatusCodes", "Residence_Status_Code", "asc");
  const [selectedStatusCode, setSelectedStatusCode] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [sortModel, setSortModel] = useState([]);
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 100,
    page: 0,
  });
  const pageName = "Residence Status Codes";

  // Custom filter implementation
  const {
    filteredData,
    filterModel,
    setFilterModel,
    resetFilters,
    hasActiveFilters,
    isFiltering,
  } = useCustomFilter(residenceStatusCodes || [], residenceStatusCodesFilters);

  const columns = useMemo(
    () => [
      {
        field: "Residence_Status",
        headerName: "Residence Status",
        flex: 1,
        minWidth: 150,
      },
      {
        field: "Residence_Status_Code",
        headerName: "Residence Status Code",
        flex: 1,
        minWidth: 180,
      },
    ],
    [],
  );

  // Prepare data for DataGrid
  const rows = useMemo(() => {
    if (!filteredData || !Array.isArray(filteredData)) return [];
    return filteredData.map((statusCode, index) => ({
      id: statusCode.id || index, // Use statusCode.id if available, otherwise use index
      ...statusCode,
    }));
  }, [filteredData]);

  const handleRowClick = (params) => {
    if (canEdit) {
      setSelectedStatusCode(params.row);
      setEditDialogOpen(true);
    }
  };

  const handleDialogClose = () => {
    setEditDialogOpen(false);
    setSelectedStatusCode(null);
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  const handleRecordUpdated = () => {
    setSnackbarMessage("Record has been updated successfully!");
    setSnackbarSeverity("success");
    setSnackbarOpen(true);
    setEditDialogOpen(false);
  };

  const handleRecordAdded = () => {
    setSnackbarMessage("Record has been added successfully!");
    setSnackbarSeverity("success");
    setSnackbarOpen(true);
  };

  const handleBulkActionComplete = (success, message) => {
    setSnackbarMessage(
      message ||
        (success ? "Bulk operation successful." : "Bulk operation failed."),
    );
    setSnackbarSeverity(success ? "success" : "error");
    setSnackbarOpen(true);
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <p>
          Error fetching status codes:{" "}
          {typeof error === "string"
            ? error
            : error?.message || "Unknown error"}
        </p>
      </Box>
    );
  }

  return (
    <Container maxWidth={false} sx={{ height: "100vh", paddingTop: "50px" }}>
      <div style={{ height: "100%", width: "100%" }}>
        <TechPrepPortalMgtBreadCrumbs pageName={pageName} icon="faCog" />

        <CustomFilterPanel
          filterConfig={residenceStatusCodesFilters}
          onFiltersChange={setFilterModel}
          initialFilters={filterModel}
          collapsible={true}
          title="Residence Status Codes Filters"
          resultCount={filteredData?.length || 0}
          totalCount={residenceStatusCodes?.length || 0}
          isSearching={isFiltering}
        />

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
            flexWrap: "wrap",
          }}
        >
          {canEdit && <Add onRecordAdded={handleRecordAdded} />}
          {canEdit && (
            <EditUpload
              collectionName="ResidenceStatusCodes"
              onActionComplete={handleBulkActionComplete}
              exportColumnOrder={columns.map((col) => col.field)}
              exportData={residenceStatusCodes}
              templateFields={columns.map((col) => col.field)}
            />
          )}
          {canEdit && (
            <ClearCollectionButton collectionName="ResidenceStatusCodes" />
          )}
        </Box>

        <Box sx={{ height: "70vh", width: "100%" }}>
          <AppDataGrid
            rows={rows}
            columns={columns}
            loading={loading}
            slots={{ toolbar: SimpleToolbar }}
            sortModel={sortModel}
            onSortModelChange={setSortModel}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[25, 50, 100, 200]}
            disableRowSelectionOnClick
            onRowClick={handleRowClick}
            sx={{
              "& .MuiDataGrid-row:hover": {
                cursor: canEdit ? "pointer" : "default",
              },
            }}
          />
        </Box>
      </div>
      {selectedStatusCode && canEdit && (
        <Edit
          open={editDialogOpen}
          onClose={handleDialogClose}
          statusCodeDetail={selectedStatusCode}
          onRecordUpdated={handleRecordUpdated}
        />
      )}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
          variant="filled"
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ResidenceStatusCodes;
